import styled, { css } from 'styled-components';

import { ACQ, CHANNEL_CARD_TONE, GRADIENTS } from '../../theme/tokens';
import type { ChannelState } from '../../theme/tokens';
import { Text } from '../Text/Text';

export type MetricSurface = 'plain' | 'panel';

/** Responsive grid for a set of MetricCards (CPA + today's-performance tiles). */
export const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

// Outer card surface. Active = the zinc featureCard (32% black wash over #18181b)
// with a hairline; exhausted / nearing-budget swap in the health glow + a colour-
// tinted border (Figma channel-detail CPA + budget cards).
//
// `raised` swaps the active card's subtly-darkened featureCard fill for the plain
// cardBase surface, so the card reads as a lifted tile when it sits inside a
// darker grouping container (the channel-detail top row) rather than on the page:
// on cardOuter the featureCard fill is the same shade as the container and would
// vanish, and in light theme it's the white-card-on-grey-container pattern. The
// tinted (exhausted / nearing-budget) states keep their glow + border, which read
// on any surface, so `raised` only affects the active branch.
const toneSurface = (tone: ChannelState, raised: boolean) =>
  tone === 'active'
    ? css`
        background: ${raised ? ACQ.cardBase : GRADIENTS.featureCard};
      `
    : css`
        background: ${CHANNEL_CARD_TONE[tone].glow};
        border: 1px solid ${CHANNEL_CARD_TONE[tone].border};
      `;

export const Root = styled.div<{ $muted: boolean; $tone: ChannelState; $raised: boolean }>`
  ${(p) => toneSurface(p.$tone, p.$raised)}
  border-radius: 16px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  opacity: ${(p) => (p.$muted ? 0.5 : 1)};
`;

export const Label = styled(Text)`
  padding: 0 5px;
  font-size: 14px;
  font-weight: 300;
  color: ${ACQ.t300};
`;

/** Inner value block. `panel` adds the washed zinc fill (performance grid); the
 * CPA card uses `plain` — same 12px inset, no fill. */
export const ValueBox = styled.div<{ $surface: MetricSurface }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border-radius: 16px;
  ${(p) =>
    p.$surface === 'panel' &&
    css`
      background: ${ACQ.panel};
    `}
`;

export const Value = styled.div`
  font-size: 24px;
  font-weight: 700;
  line-height: 1.1;
  color: ${ACQ.t50};
`;
